<template>
  <div class="row mb-3 w-100 mx-0">
    <div v-for="field in fields" :key="field.key" :class="colClass + ' px-2'">
      <label class="d-block">
        {{ field.label }}
        <el-tooltip v-if="field.tooltip" :content="field.tooltip" placement="top">
          <i class="fa-solid fa-circle-info ml-1" style="color: #909399; font-size: 12px; cursor: help;"></i>
        </el-tooltip>
      </label>
      <template v-if="field.type === 'checkbox'">
        <input
          type="checkbox"
          v-model="internalRecord[field.key]"
          class="custom-checkbox"
          :disabled="field.disabled !== undefined ? field.disabled : disabled"
        />
      </template>
      <template v-else-if="field.type === 'date'">
        <el-date-picker
          v-model="internalRecord[field.key]"
          type="date"
          :placeholder="field.placeholder || 'MM/DD/YYYY'"
          value-format="yyyy-MM-dd"
          format="MM/dd/yyyy"
          :editable="true"
          class="w-100"
          :disabled="field.disabled !== undefined ? field.disabled : disabled"
        />
      </template>
      <template v-else-if="field.type === 'datetime'">
        <input
          type="datetime-local"
          :value="getNestedValue(internalRecord, field.key)"
          @input="
            setNestedValue(internalRecord, field.key, $event.target.value)
          "
          class="form-control date-input"
          :placeholder="field.placeholder || field.label"
          :disabled="field.disabled !== undefined ? field.disabled : disabled"
        />
      </template>
      <template v-else-if="field.type === 'textarea'">
        <el-input
          type="textarea"
          v-model="internalRecord[field.key]"
          :placeholder="field.placeholder || field.label"
          class="w-100"
          :rows="field.rows || 3"
          :disabled="field.disabled !== undefined ? field.disabled : disabled"
        />
      </template>
      <template v-else-if="field.type === 'select'">
        <AIVSelect
          v-model="internalRecord[field.key]"
          :options="getFieldOptions(field)"
          :placeholder="field.placeholder || field.label"
          :autocomplete="field.autocomplete || 'off'"
          :disabled="field.disabled !== undefined ? field.disabled : disabled"
          :custom-style="field.customStyle || {}"
          @input="(val) => field.onChange && field.onChange(val)"
        />
      </template>
      <template v-else-if="field.type === 'multiselect'">
        <AIVSelect
          v-model="internalRecord[field.key]"
          :options="getFieldOptions(field)"
          :placeholder="field.placeholder || field.label"
          :multiSelect="true"
          :disabled="field.disabled !== undefined ? field.disabled : disabled"
          :custom-style="field.customStyle || {}"
          @input="(val) => field.onChange && field.onChange(val)"
        />
      </template>
      <template v-else-if="field.type === 'time'">
        <AIVTimeSelect
          v-model="internalRecord[field.key]"
          :disabled="field.disabled !== undefined ? field.disabled : disabled"
          :allowExactMinutes="field.allowExactMinutes !== undefined ? field.allowExactMinutes : false"
          @ok-clicked="handleTimeOkClick(field.key)"
        />
      </template>
      <template v-else-if="field.type === 'ssn'">
        <div class="position-relative">
          <input
            id="ssn"
            :type="ssnInputType"
            :value="formattedSSN"
            @input="formatSSN"
            maxlength="11"
            placeholder="9 Digit SSN..."
            class="form-control ssn-input"
            :disabled="field.disabled !== undefined ? field.disabled : disabled"
          />
          <i
            v-if="!(field.disabled !== undefined ? field.disabled : disabled)"
            :class="ssnVisible ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"
            @click="toggleSSNVisibility"
            class="position-absolute"
            style="
              top: 50%;
              right: 12px;
              transform: translateY(-50%);
              cursor: pointer;
              color: #ccc;
            "
            aria-hidden="true"
          ></i>
        </div>
      </template>
      <template v-else>
        <el-input
          v-model="internalRecord[field.key]"
          :placeholder="field.placeholder || field.label"
          class="w-100"
          :disabled="field.disabled !== undefined ? field.disabled : disabled"
        />
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: "ModalRow",
  props: {
    record: {
      type: Object,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
      validator(arr) {
        return (
          arr.length >= 1 &&
          arr.length <= 3 &&
          arr.every((f) => f.label && f.key)
        );
      },
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      internalRecord: { ...this.record },
      ssnVisible: false,
      ssnInputType: "password",
      ssn: "",
    };
  },
  computed: {
    colClass() {
      return `col-md-${12 / this.fields.length}`;
    },
    formattedSSN() {
      const ssn = this.internalRecord?.ssn || "";
      const numbers = ssn.replace(/\D/g, "").slice(0, 9);
      const parts = [];
      if (numbers.length > 0) parts.push(numbers.slice(0, 3));
      if (numbers.length > 3) parts.push(numbers.slice(3, 5));
      if (numbers.length > 5) parts.push(numbers.slice(5, 9));
      return parts.join("-");
    },
  },
  watch: {
    record: {
      handler(newVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(this.internalRecord)) {
          this.internalRecord = { ...newVal };
        }
      },
      deep: true,
    },
    internalRecord: {
      handler(newVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(this.record)) {
          this.$emit("update:record", { ...newVal });
        }
      },
      deep: true,
    },
  },
  methods: {
    toggleSSNVisibility() {
      switch (this.ssnInputType) {
        case "password":
          this.ssnInputType = "text";
          break;
        case "text":
          this.ssnInputType = "password";
          break;
      }
    },
    formatSSN(event) {
      const raw = event.target.value.replace(/\D/g, "").slice(0, 9);
      this.$set(this.internalRecord, "ssn", raw);
    },
    getNestedValue(obj, path) {
      return path.split(".").reduce((o, k) => (o || {})[k], obj);
    },
    setNestedValue(obj, path, value) {
      const keys = path.split(".");
      const lastKey = keys.pop();
      const target = keys.reduce((o, k) => {
        if (!o[k]) o[k] = {};
        return o[k];
      }, obj);
      this.$set(target, lastKey, value);
    },
    getFieldOptions(field) {
      if (!field.options || !Array.isArray(field.options)) {
        return [];
      }

      return field.options.map(option => {
        if (typeof option === 'object' && option !== null) {
          return {
            id: option[field.optionValue || 'id'],
            name: option[field.optionLabel || 'name']
          };
        }
        return { id: option, name: option };
      });
    },
    handleTimeOkClick(fieldKey) {
      this.$emit('time-ok-clicked', fieldKey);
    },
  },
};
</script>
<style scoped>
label.d-block {
  margin-bottom: 2px !important;
}
.custom-checkbox {
  margin: 5px 0 0;
  -webkit-appearance: none;
  appearance: none;
  width: 26px;
  height: 26px;
  border: 1px solid #bdbdbd;
  border-radius: 0.25em;
  position: relative;
  cursor: pointer;
}
.custom-checkbox:checked {
  background-color: #656565;
}
.custom-checkbox:checked::after {
  content: "";
  position: absolute;
  left: 0.55em;
  top: 0.3em;
  width: 0.5em;
  height: 0.9em;
  border: solid white;
  border-width: 0 0.25em 0.25em 0;
  transform: rotate(45deg);
}
/*noinspection CssUnusedSymbol */
:deep(.el-textarea__inner) {
  background-color: #656565 !important;
  color: #ffffff !important;
  font-size: 14px !important;
  font-weight: normal !important;
  word-break: normal;
  overflow-wrap: break-word;
  white-space: pre-wrap;
}

:deep(.el-textarea__inner:focus) {
  border-color: #ffffff !important;
  box-shadow: none !important;
  background-color: #656565 !important;
  color: #ffffff !important;
  font-size: 14px !important;
  font-weight: normal !important;
}

/* Maximum specificity to override any other styles */
:deep(.el-form-item .el-textarea .el-textarea__inner:focus),
:deep(.el-textarea .el-textarea__inner:focus),
:deep(textarea.el-textarea__inner:focus) {
  border-color: #ffffff !important;
  box-shadow: none !important;
  background-color: #656565 !important;
  color: #ffffff !important;
  font-size: 14px !important;
  font-weight: normal !important;
  -webkit-text-fill-color: #ffffff !important;
}

/* AIVRow component should expand fully in modals */
.row {
  width: 100% !important;
}

/* Disabled field styling to match AIVSelect disabled styling */
/* AIVSelect uses #8a8a8a with 0.7 opacity, which equals #777777 on #4a4a4a background */
:deep(.el-input.is-disabled .el-input__inner) {
  background-color: #4a4a4a !important;
  color: #b0b0b0 !important;
  border-color: #6a6a6a !important;
  cursor: default !important;
}

:deep(.el-textarea.is-disabled .el-textarea__inner) {
  background-color: #4a4a4a !important;
  color: #b0b0b0 !important;
  border-color: #6a6a6a !important;
  cursor: default !important;
}

.form-control:disabled,
.date-input:disabled {
  background-color: #4a4a4a !important;
  color: #b0b0b0 !important;
  border-color: #6a6a6a !important;
  cursor: default !important;
}

.custom-checkbox:disabled {
  background-color: #4a4a4a !important;
  border-color: #6a6a6a !important;
  cursor: default !important;
}

.ssn-input:disabled {
  background-color: #4a4a4a !important;
  color: #b0b0b0 !important;
  border-color: #6a6a6a !important;
  cursor: default !important;
}
</style>
